import * as React from 'react';
import Avatar from '@material-ui/core/Avatar';
import ListItem, { ListItemProps } from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText, { ListItemTextProps } from '@material-ui/core/ListItemText';
import Truncate from 'react-truncate';
import Typography from '@material-ui/core/Typography';
import { AvatarBadge } from './AvatarBadge';
import { NavLink } from './NavLink';
import { NavLinkProps } from 'react-router-dom';
import { Theme, withStyles, WithStyles } from '@material-ui/core/styles';

const styles = (theme: Theme) => ({
  root: {
    padding: theme.spacing(0, 2)
  },
  avatarBadge: {
    overflow: 'visible'
  }
});

export type ContentProps = {
  icon: JSX.Element;
  title?: string;
  primaryText: string;
  className?: string;
  style?: React.CSSProperties;
  photoUrl?: string | null;
  secondaryAction?: JSX.Element;
  secondaryTypographyProps?: ListItemTextProps<'span', 'span'>['secondaryTypographyProps'];
};

export type OwnProps = {
  to?: string;
  onClick?: () => void;
  href?: string;
  target?: string;
  maxLinesPrimary?: number;
  maxLinesSecondary?: number;
  badgeContent?: number;
};

export type Props = ContentProps & ListItemProps & OwnProps;

type State = {
  hasImgError: boolean;
};

class IconListItemContentComponent extends React.Component<Props & WithStyles<typeof styles>, State> {
  constructor(props: Props & WithStyles<typeof styles>) {
    super(props);
    this.state = {
      hasImgError: false
    };
  }

  handleImgError = () => {
    this.setState({ hasImgError: true });
  };

  render() {
    const {
      style,
      className,
      icon,
      title,
      primaryText,
      photoUrl,
      children,
      secondaryAction,
      secondaryTypographyProps,
      maxLinesPrimary,
      maxLinesSecondary,
      badgeContent,
      classes
    } = this.props;
    return (
      <React.Fragment>
        <ListItemAvatar>
          {photoUrl && !this.state.hasImgError ? (
            <Avatar
              style={style}
              src={photoUrl}
              alt={primaryText}
              className={className}
              onError={this.handleImgError}
              title={title || primaryText}
            />
          ) : (
            <Avatar
              style={style}
              className={`${className} ${badgeContent ? classes.avatarBadge : ''}`}
              title={title || primaryText}
            >
              {badgeContent ? <AvatarBadge badgeContent={badgeContent}>{icon}</AvatarBadge> : icon}
            </Avatar>
          )}
        </ListItemAvatar>
        <ListItemText
          classes={{ root: classes.root }}
          primary={
            maxLinesPrimary ? (
              <Typography>
                <Truncate lines={maxLinesPrimary}>{primaryText}</Truncate>
              </Typography>
            ) : (
              primaryText
            )
          }
          secondary={
            maxLinesSecondary ? (
              <Typography>
                <Truncate lines={maxLinesSecondary}>{children}</Truncate>
              </Typography>
            ) : (
              children
            )
          }
          secondaryTypographyProps={secondaryTypographyProps}
        />
        {secondaryAction && <ListItemSecondaryAction>{secondaryAction}</ListItemSecondaryAction>}
      </React.Fragment>
    );
  }
}

export const IconListItemContent = withStyles(styles)(IconListItemContentComponent);

const Link = React.forwardRef<HTMLAnchorElement, ListItemProps<'a', Omit<NavLinkProps, 'to'> & OwnProps>>(
  ({ to, href, target, ...props }, ref) =>
    to ? (
      <NavLink ref={ref} to={to} {...props}>
        {props.children}
      </NavLink>
    ) : (
      <a ref={ref} href={href} target={target} {...props}>
        {props.children}
      </a>
    )
);

export const IconListItem: React.FC<Props> = ({ classes, divider, to, onClick, href, target, ref, ...contentProps }) =>
  to || href ? (
    <ListItem button classes={classes} divider={divider} component={Link} to={to} href={href} target={target}>
      <IconListItemContent {...contentProps} />
    </ListItem>
  ) : (
    <ListItem button={!!onClick as true} classes={classes} divider={divider} onClick={onClick}>
      <IconListItemContent {...contentProps} />
    </ListItem>
  );
